import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Radio,
  Typography,
  DatePicker,
  Select,
  Divider,
  Button,
  AutoComplete,
  Image,
} from "antd";
import moment from "moment";
import debounce from "lodash/debounce";

import productOfferFree from "../../../assets/productOfferFree.png";
import Popup from "../../../components/Popup";
import { DateFormat, errorMessage, getApi, saveApi } from "../../../helpers";
import UploadImage from "../../../components/UploadImage";
import PreviewBanner from "../../../components/PreviewBanner";
import { DeleteOutlined } from "@ant-design/icons";
import { StoreContext } from "../../../context";
import "./index.css";

export default function AdPopup({
  visible = false,
  title = "",
  data = {},
  cancelHandler,
  saveHandler,
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState("url");
  const [options, setOptions] = useState([]);
  const [stores, setStores] = useState([]);
  const [PVId, setPVId] = useState(
    data.product_id
      ? { product_id: data.product_id, variant_id: data.variant_id }
      : {}
  );
  const [imageFile, setImageFile] = useState(
    data.banner_image ? { url: data.banner_image } : {}
  );
  const [form] = Form.useForm();

  useEffect(() => {
    if (data.type) {
      setActionType(data.type);
      if (data.type === "category") getOptionsHandler();
      else if (data.type === "sub_category")
        getOptionsHandler("getSubCategory");
      else if (data.type === "product")
        getOptionsHandler("getVariants", data.product_name);
    }

    getStores();
  }, []);

  useEffect(() => {
    if (storeId) {
      form.setFieldsValue({ store_id: storeId });
    }
  }, [storeId, form]);

  async function getStores() {
    const res = await getApi({}, "getStores", "dashboard/");
    if (res.data && res.data.length) {
      setStores(res.data);
    }
  }

  async function offerByChangeHandler(e) {
    if (e.target.value) {
      setOptions([]);
      setActionType(e.target.value);
      if (e.target.value === "product")
        setOptions([{ label: "Type to Search Product", disabled: true }]);
      else if (e.target.value === "category") getOptionsHandler();
      else if (e.target.value === "sub_category")
        getOptionsHandler("getSubCategory");
    }
  }

  async function getOptionsHandler(apiName = "getCategory", keyword = "", type= "admin") {
    setLoading(true);
    setOptions([{ label: "Searching...", disabled: true }]);
    const res = await getApi({ size: 100, keyword: keyword }, apiName);
    if (res.data && res.data.length) setOptions(res.data);
    else setOptions([{ label: "No Such Product", disabled: true }]);
    setLoading(false);
  }

  const debouncedSearch = useCallback(
    debounce((nextValue) => getOptionsHandler("getVariants", nextValue), 1200),
    []
  );

  function saveAd() {
    form
      .validateFields()
      .then((values) => {
        if (imageFile.url) {
          values = {
            ...values,
            valid_from: DateFormat(values.validity[0], "YYYY-MM-DD"),
            valid_till: DateFormat(values.validity[1], "YYYY-MM-DD"),
          };
          if (data.banner_image && imageFile.file) {
            values = { ...values, file: imageFile.file, delete: true };
          } else if (imageFile.file)
            values = { ...values, file: imageFile.file };
          if (values.sub_category_id) {
            let scc = values.sub_category_id.split(";");
            values = {
              ...values,
              sub_category_id: scc[0],
              category_id: scc[1],
            };
          }
          if (PVId.product_id) values = { ...values, ...PVId };
          saveHandler(values);
        } else errorMessage("Please upload banner image !");
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  }

  return (
    <Popup
      width="75%" // Increased from 68% to 75% to accommodate wider preview
      title={title}
      visible={visible}
      cancelHandler={() => (form.resetFields(), cancelHandler())}
      preSaveCheck={() =>
        form
          .validateFields()
          .then(() => true)
          .catch(() => false)
      }
      saveHandler={saveAd}
    >
      <Form
        className="banner-popup-form"
        requiredMark={false}
        form={form}
        layout="vertical"
        name="offer"
        initialValues={{ ...data, store_id: storeId || data.store_id }}
      >
        <Row className="banner-popup-layout" gutter={[24, 0]}>
          <Col xs={24} md={13} className="banner-popup-form-col">
            <div className="banner-popup-section">
              <Row className="banner-popup-fields" gutter={[12, 0]}>
              <Col xs={24}>
                {imageFile.url ? (
                  <Col xs={24}>
                    <div className="banner-popup-image-actions">
                      <Button onClick={() => setImageFile({})} size="small">
                        <Typography.Text className="banner-popup-remove-image-text">
                          <DeleteOutlined /> Remove Image
                        </Typography.Text>
                      </Button>
                    </div>
                    <Image
                      width="100%"
                      // height={260}
                      style={{ objectFit: "contain" }}
                      src={imageFile.url}
                      alt="img"
                    />
                  </Col>
                ) : (
                  <UploadImage
                    bannerImage
                    uploadLater
                    beforeUpload={(file) => {
                      const isLt2M = file.size / 1024 / 1024 < 5;
                      if (!isLt2M) {
                        errorMessage("Image must smaller than 5MB!");
                      }
                      return isLt2M;
                    }}
                    uploadLaterHandler={({ file, onSuccess }) => {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        setImageFile({ file: file, url: e.target.result });
                      };
                      reader.readAsDataURL(file);
                      onSuccess("Ok");
                    }}
                    title="Banner Image"
                    done={false}
                  />
                )}
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="name"
                  className="banner-popup-tight-item"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the name of ad!",
                    },
                  ]}
                >
                  <Input placeholder="Ex. Banner Name" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="banner_type"
                  className="banner-popup-tight-item"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the banner type!",
                    },
                  ]}
                  initialValue={data.banner_type || "homepage"}
                >
                  <Input placeholder="homepage" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  style={{ marginTop: 5, marginBottom: 8 }}
                  className="banner-popup-type-item"
                  name="type"
                  initialValue={data.type ? data.type : "url"}
                >
                  <Radio.Group onChange={offerByChangeHandler}>
                    <Radio value={"url"}>Url</Radio>
                    <Radio value={"product"}>Product</Radio>
                    <Radio value={"category"}>Category</Radio>
                    <Radio value={"sub_category"}>Sub Category</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              {actionType === "url" ? (
                <Col xs={24}>
                  <Form.Item
                    name="url"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the url!",
                      },
                    ]}
                  >
                    <Input placeholder="Ex. https://rapsap.com" />
                  </Form.Item>
                </Col>
              ) : actionType.includes("category") ? (
                <Col span={24}>
                  <Form.Item
                    name={
                      actionType === "category"
                        ? "category_id"
                        : "sub_category_id"
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please select the Category!",
                      },
                    ]}
                  >
                    <Select
                      loading={loading}
                      placeholder="Ex. Dairy"
                      showSearch
                      dropdownStyle={{ maxHeight: 100 }}
                      listHeight={96}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase())
                      }
                    >
                      {options.map((optn) => (
                        <Select.Option
                          key={optn.sub_category_id || optn.category_id}
                          value={
                            optn.sub_category_id
                              ? optn.sub_category_id + ";" + optn.category_id
                              : optn.category_id
                          }
                        >
                          {optn.sub_category_name
                            ? optn.category_name +
                              " - " +
                              optn.sub_category_name
                            : optn.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              ) : (
                <Col span={24}>
                  <Form.Item
                    name="pv_name"
                    // label="Select Product"
                    rules={[
                      {
                        validator: () => {
                          if (data.product_id || PVId.product_id) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Please select the Product!")
                          );
                        },
                      },
                    ]}
                  >
                    <AutoComplete
                      showArrow
                      loading={loading}
                      dropdownStyle={{ maxHeight: 100 }}
                      listHeight={96}
                      options={options.map((v) => {
                        if (v.label) {
                          return v;
                        }
                        return (v = {
                          value: v.product_name + " - " + v.variant_name,
                          key: v.variant_id,
                          pid: v.product_id,
                        });
                      })}
                      onSelect={(v, vObj) =>
                        setPVId({ product_id: vObj.pid, variant_id: vObj.key })
                      }
                      onSearch={(value) =>
                        value.length > 1 && debouncedSearch(value)
                      }
                      placeholder="Ex. Milk - 1 Ltr"
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24}>
                <Form.Item
                  name="validity"
                  rules={[
                    {
                      required: true,
                      message: "Please select the validity!",
                    },
                  ]}
                  initialValue={
                    data.valid_from &&
                    data.valid_till && [
                      moment(data.valid_from),
                      moment(data.valid_till),
                    ]
                  }
                >
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    placeholder={["22-06-2021", "28-06-2021"]}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="description">
                  <Input.TextArea
                    showCount
                    maxLength={255}
                    placeholder="Ex. Url banner for new users."
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="store_id"
                  className="banner-popup-store-item"
                  rules={[
                    {
                      required: true,
                      message: "Please select store!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Store"
                    showSearch
                    optionFilterProp="children"
                    disabled={!!storeId}
                  >
                    {stores.map((store) => (
                      <Select.Option key={store.store_id} value={store.store_id}>
                        {store.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              </Row>
            </div>
          </Col>
          <Col xs={0} md={{ offset: 1, span: 10 }} className="banner-popup-preview-col">
            <div className="banner-popup-preview-wrap">
              <PreviewBanner src={imageFile.url} />
            </div>
          </Col>
        </Row>
      </Form>
    </Popup>
  );
}
